/**
 * This file should be shared between the webshop-backend and the webshop-frontend.
 * Whenever a modification is made to this file, make sure to copy its content and paste it in:
 *  - webshop-backend/src/routes/shared_types.ts
 *  - webshop-frontend/src/types/api_shared_types.ts
 */

import { Language } from './cms_shared_types';

// Cases

export interface CreateCaseParams {
  referenceNumber: string;
  name: string;
  email: string;
  phoneNumber: string;
  date?: string;
  questions: {
    question: string;
    answer: boolean | null;
  }[];
  comment?: string;
}

// Prices

export interface PriceProduct {
  code: string;
  quantity: string;
  timeQuantity: string;
  musicClass: string;
}

export interface PriceResponse {
  priceYearInclVat: number;
  priceYearExclVat: number;
}

export interface CartPriceRequest {
  products: ProductWithDiscount[];
}

export interface ProductWithDiscount {
  code: string;
  triggerDiscount?: string[];
  discountReplacementProduct?: string;
  quantity: string;
  timeQuantity: string;
  musicClass: string;
  priceYearInclVat: number;
  priceYearExclVat: number;
}

// Agreements

type AddonProviderIdentifier = 'SOUNDTRACK' | 'UNKNOWN';

type SoundtrackSubscriptionPlan = 'ESSENTIAL' | 'UNLIMITED';

interface SoundtrackRegistrationData {
  plan: SoundtrackSubscriptionPlan;
}

// Can be extended with the | operator for other providers.
type ProviderRequiredData = SoundtrackRegistrationData;

export interface AgreementResponse {
  access_link: string;
  contractId: string;
}

export interface ShopForm {
  products: ShopFormProduct[];
  addons: ShopFormAddon[];
  category: string;
  subcategory: string;
  comments: string;
  information: InformationFormInputs;
  invoice: InvoiceFormInputs;
  contacts: ContactFormInputs;
  reviewRequired: boolean;
  referenceNumber?: string | null;
  soundtrackEmail?: string;
  language?: Language;
}

export interface ShopFormProduct {
  code: string;
  quantity: string;
  timeQuantity: string;
  musicClass: string;
  priceYearInclVat: number;
  priceYearExclVat: number;
  name: string;
  summary: string;
  reporting: boolean;
  triggerDiscount?: string[];
  discountReplacementProduct?: string;
}

export interface ShopFormAddon {
  code: string;
  name: string;
  description: string;
  forcesMonthlyInvoicing: boolean;
  monthlyPriceExclVat?: number;
  monthlyPriceInclVat?: number;
  providerIdentifier: AddonProviderIdentifier;
  providerRequiredData: ProviderRequiredData;
}

export interface InformationFormInputs {
  businessNameInput: string;
  businessCategoryInput: string;
  businessAddressInput: string;
  businessPostNumberInput: string;
  businessCityInput: string;
}

export interface InvoiceFormInputs {
  organisationNumberInput: string;
  companyNameInput: string;
  companyAddressInput: string;
  companyPostNumberInput: string;
  companyCityInput: string;
  companyCountryInput: string;

  sameInvoiceAddressInput: boolean;

  invoiceCareOfInput: string;
  invoiceAddressInput: string;
  invoicePostNumberInput: string;
  invoiceCityInput: string;
  invoiceCountryInput: string;

  startDateInput: string;
  invoicePeriodInput: string;
  emailAddressPdfInput: string;
  selectedInvoiceMethod: InvoiceMethod;
  ediFormat?: EdiFormat | '';
  ediReference?: string;
  peppolId?: string;
  ediOperator?: string;
  gln?: string;
}

export interface ContactFormInputs {
  contactFirstNameInput: string;
  contactLastNameInput: string;
  contactEmailInput: string;
  contactTelephoneNumberInput: string;
  soundtrackEmailInput: string;

  sameReportingContactInput: boolean;
  sameSoundtrackEmailInput: boolean;

  reportingFirstNameInput: string;
  reportingLastNameInput: string;
  reportingEmailInput: string;
  reportingTelephoneNumberInput: string;

  acceptTermsAndConditions: boolean;
}

//Address lookup
export interface AddressSuggestion {
  streetName: string;
  streetNumber: string;
  zipcode: string;
  city: string;
}

// Invoice

export enum InvoiceMethod {
  EMAIL = 'email',
  EDI = 'edi',
  PAPER = 'paper',
}

export enum EdiFormat {
  PEPPOL = 'peppol',
  SVEFAKTURA = 'svefaktura',
}
